import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, ChevronDown } from 'lucide-react';

const styles = [
  { id: 'lower-third', name: 'Lower Third', description: 'Classic bottom banner' },
  { id: 'headline', name: 'Headline', description: 'Large centered text' },
  { id: 'pill', name: 'Pill', description: 'Compact rounded badge' },
  { id: 'ribbon', name: 'Ribbon', description: 'Edge-to-edge banner' }
];

const placements = [
  { id: 'bottom', name: 'Bottom', description: 'Lower part of screen' },
  { id: 'top', name: 'Top', description: 'Upper part of screen' },
  { id: 'center', name: 'Center', description: 'Middle of screen' },
  { id: 'left', name: 'Left', description: 'Left side of screen' },
  { id: 'right', name: 'Right', description: 'Right side of screen' }
];

// Emojis to insert into message text
const emojis = ['💡', '🎯', '🔥', '✨', '💬', '📢', '🎉', '👋', '💪', '🚀', '⭐', '❤️', '👍', '🙌', '🤝', '💯', '🏆', '🎁', '📣', '💥', '😀', '😊', '🙏', '👏'];

const presetColors = [
  { bg: '#6a1fbf', text: '#ffffff' },
  { bg: '#00c853', text: '#ffffff' },
  { bg: '#ff6b6b', text: '#ffffff' },
  { bg: '#001233', text: '#ffffff' },
  { bg: '#ffffff', text: '#001233' },
  { bg: '#ffd93d', text: '#001233' },
];

// Custom Select Component - uses portal to avoid overflow clipping
const Select = ({ value, onChange, options, placeholder, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const updatePosition = () => {
      const rect = ref.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.id === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
      >
        <span>{selectedOption?.name || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => { onChange(option.id); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.id === value ? 'text-purple-400 bg-white/5' : 'text-gray-300'
              }`}
            >
              {option.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// Custom Switch Component
const Switch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Preview Component
function TalkingPointPreview({ text, style, color, background }) {
  const baseStyles = {
    backgroundColor: background,
    color: color
  };

  switch (style) {
    case 'headline':
      return (
        <div
          className="px-6 py-4 rounded-xl text-center text-xl font-bold shadow-lg"
          style={baseStyles}
        >
          {text}
        </div>
      );
    case 'pill':
      return (
        <div
          className="px-4 py-2 rounded-full text-sm font-medium shadow-lg inline-flex items-center gap-2"
          style={baseStyles}
        >
          {text}
        </div>
      );
    case 'ribbon':
      return (
        <div
          className="w-full px-6 py-3 text-center font-medium shadow-lg"
          style={baseStyles}
        >
          {text}
        </div>
      );
    default: // lower-third
      return (
        <div
          className="px-5 py-3 rounded-lg shadow-lg inline-flex items-center gap-2"
          style={baseStyles}
        >
          <span className="font-medium">{text}</span>
        </div>
      );
  }
}

export default function TalkingPointModal({
  isOpen,
  onClose,
  onSave,
  talkingPoint = null,
  folders = [],
  currentFolder = null,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [formData, setFormData] = useState({
    text: '',
    style: 'lower-third',
    placement: 'bottom',
    color: '#ffffff',
    background: '#6a1fbf',
    auto_hide_ms: 0,
    order: 0,
    folder_id: currentFolder?.id || ''
  });

  const [autoHideEnabled, setAutoHideEnabled] = useState(false);
  const [autoHideSeconds, setAutoHideSeconds] = useState(5);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Only reset form when modal is opened fresh (not during re-renders)
  useEffect(() => {
    // Only initialize when modal opens and hasn't been initialized yet
    if (isOpen && !hasInitialized) {
      if (talkingPoint) {
        setFormData({
          text: talkingPoint.text || '',
          style: talkingPoint.style || 'lower-third',
          placement: talkingPoint.placement || 'bottom',
          color: talkingPoint.color || '#ffffff',
          background: talkingPoint.background || '#6a1fbf',
          auto_hide_ms: talkingPoint.auto_hide_ms || 0,
          order: talkingPoint.order || 0,
          folder_id: talkingPoint.folder_id || currentFolder?.id || ''
        });
        setAutoHideEnabled(!!talkingPoint.auto_hide_ms);
        setAutoHideSeconds((talkingPoint.auto_hide_ms || 5000) / 1000);
      } else {
        setFormData({
          text: '',
          style: 'lower-third',
          placement: 'bottom',
          color: '#ffffff',
          background: '#6a1fbf',
          auto_hide_ms: 0,
          order: 0,
          folder_id: currentFolder?.id || ''
        });
        setAutoHideEnabled(false);
        setAutoHideSeconds(5);
      }
      setHasInitialized(true);
    }

    // Reset initialization flag when modal closes
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen, talkingPoint, currentFolder, hasInitialized]);

  const handleSave = () => {
    onSave({
      name: formData.text.substring(0, 50) || 'Talking Point',
      ...formData,
      auto_hide_ms: autoHideEnabled ? autoHideSeconds * 1000 : 0
    });
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#0a1628] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-white">
                  {talkingPoint ? 'Edit Talking Point' : 'New Talking Point'}
                </h2>
                <p className="text-xs text-gray-400">Create an on-screen message</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
            {readOnly && (
              <div className="flex items-start gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            {/* Folder Selection */}
            {!skipFolderSelect && folders.length > 0 && (
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Folder</label>
                <Select
                  value={formData.folder_id}
                  onChange={(v) => setFormData({ ...formData, folder_id: v })}
                  options={folders.map(f => ({ id: f.id, name: f.name }))}
                  placeholder="Select folder..."
                />
              </div>
            )}

            {/* Text Input */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Message</label>
              <textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Enter your talking point message..."
                disabled={readOnly}
                className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
            </div>

            {/* Emoji Selector - Click to insert into message */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Insert Emoji</label>
              <div className="flex flex-wrap gap-2">
                {emojis.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => setFormData({ ...formData, text: formData.text + emoji })}
                    className="w-9 h-9 rounded-lg border text-lg transition-all border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#6a1fbf] hover:scale-110"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Style Selector */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Style</label>
              <div className="grid grid-cols-2 gap-2">
                {styles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setFormData({ ...formData, style: style.id })}
                    className={`p-3 rounded-xl text-left transition-all ${
                      formData.style === style.id
                        ? 'bg-[#6a1fbf] text-white'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <span className="text-sm font-medium block">{style.name}</span>
                    <span className="text-[10px] opacity-70">{style.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Placement Selector */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Position on Screen</label>
              <div className="grid grid-cols-3 gap-2">
                {placements.map((placement) => (
                  <button
                    key={placement.id}
                    onClick={() => setFormData({ ...formData, placement: placement.id })}
                    className={`p-2 rounded-xl text-center transition-all ${
                      formData.placement === placement.id
                        ? 'bg-[#6a1fbf] text-white'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <span className="text-xs font-medium block">{placement.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Color Presets */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Colors</label>
              <div className="flex gap-2 mb-3">
                {presetColors.map((preset, i) => (
                  <button
                    key={i}
                    onClick={() => setFormData({ ...formData, background: preset.bg, color: preset.text })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      formData.background === preset.bg ? 'border-white scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: preset.bg }}
                  />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-500 text-xs mb-1 block">Background</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.background}
                      onChange={(e) => setFormData({ ...formData, background: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border-0"
                    />
                    <input
                      value={formData.background}
                      onChange={(e) => setFormData({ ...formData, background: e.target.value })}
                      className="flex-1 px-2 bg-white/5 border border-white/10 rounded text-white text-xs uppercase h-8"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-500 text-xs mb-1 block">Text</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border-0"
                    />
                    <input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1 px-2 bg-white/5 border border-white/10 rounded text-white text-xs uppercase h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Auto-hide */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-gray-400 text-sm">Auto-hide after duration</label>
                <Switch
                  checked={autoHideEnabled}
                  onChange={setAutoHideEnabled}
                />
              </div>
              {autoHideEnabled && (
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={autoHideSeconds}
                    onChange={(e) => setAutoHideSeconds(parseInt(e.target.value) || 5)}
                    className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-center text-sm"
                  />
                  <span className="text-sm text-gray-400">seconds</span>
                </div>
              )}
            </div>

            {/* Preview */}
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Preview</label>
              <div className="bg-gray-900/50 rounded-xl p-4 min-h-[80px] flex items-end justify-center">
                <TalkingPointPreview
                  text={formData.text || 'Your message here...'}
                  style={formData.style}
                  color={formData.color}
                  background={formData.background}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                onClick={handleSave}
                disabled={!formData.text.trim()}
                className="px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {talkingPoint ? 'Save Changes' : 'Create'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
